/**
 * POST + Server-Sent Events for the assistant's text streams: the creation
 * chat brief (`/live/brief/`) and the Studio (`/studio/generate/`,
 * `/studio/revise/`). Every frame is `data: <json>` with one of
 * `{type:"activity",label}`, `{type:"delta",text}`,
 * `{type:"done",text,title?}`, `{type:"error",code,message}` (services/
 * assistant core/live/studio.py). A refusal before the stream opens arrives
 * as the JSON `{error:{code,message}}` envelope and is thrown as
 * `StudioStreamError` carrying the server's own words.
 */
import { getValidAccessTokenFromStorage } from "@weco/suite-client";

export const ASSISTANT_BASE = "/api/assistant";

export type StudioStreamEvent =
  | { type: "activity"; label: string }
  | { type: "delta"; text: string }
  | { type: "done"; text: string; title?: string }
  | { type: "error"; code: string; message: string };

export class StudioStreamError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string,
  ) {
    super(message);
  }
}

export async function assistantHeaders(json = true): Promise<Record<string, string>> {
  const token = await getValidAccessTokenFromStorage();
  const headers: Record<string, string> = {};
  if (json) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) headers["X-Timezone"] = tz;
  } catch {
    /* no Intl zone: the server counts in UTC */
  }
  return headers;
}

export async function refusalFrom(res: Response): Promise<StudioStreamError> {
  let message = "";
  let code = "error";
  try {
    const body = await res.json();
    const err = body?.error;
    if (err && typeof err === "object") {
      message = typeof err.message === "string" ? err.message : "";
      code = typeof err.code === "string" ? err.code : code;
    } else if (typeof err === "string") {
      message = err;
    } else if (typeof body?.detail === "string") {
      message = body.detail;
    }
  } catch {
    /* not JSON */
  }
  return new StudioStreamError(message, res.status, code);
}

/** Parse SSE text into events; returns the unparsed tail. */
export function parseFrames(buffer: string, onEvent: (e: StudioStreamEvent) => void): string {
  const frames = buffer.split(/\n\n/);
  const tail = frames.pop() ?? "";
  for (const frame of frames) {
    for (const line of frame.split("\n")) {
      if (!line.startsWith("data:")) continue;
      const payload = line.slice(5).trim();
      if (!payload) continue;
      try {
        onEvent(JSON.parse(payload) as StudioStreamEvent);
      } catch {
        /* a malformed frame is skipped, never fatal */
      }
    }
  }
  return tail;
}

/**
 * Stream one request. Resolves when the stream ends; `onEvent` sees every
 * event in order. Throws `StudioStreamError` for a refusal (HTTP ≥ 400) and
 * re-throws an abort as a `DOMException` named "AbortError".
 */
export async function streamStudio(
  path: string,
  body: unknown,
  onEvent: (e: StudioStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${ASSISTANT_BASE}${path}`, {
    method: "POST",
    headers: await assistantHeaders(),
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw await refusalFrom(res);
  if (!res.body) return;
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer = parseFrames(buffer + decoder.decode(value, { stream: true }), onEvent);
  }
  parseFrames(buffer + decoder.decode() + "\n\n", onEvent);
}

export const isAbort = (e: unknown) => e instanceof DOMException && e.name === "AbortError";

/** `[[file:id|Name|Page 3]]` → "Name (Page 3)" (spec `plainCitations`). */
export function plainCitations(text: string): string {
  return text.replace(/\[\[(?:file|folder):[^|\]]+\|([^|\]]+)(?:\|([^\]]+))?\]\]/g, (_m, name, loc) =>
    loc ? `${name} (${loc})` : name,
  );
}
